import { Link } from "react-router-dom";
import { AUTONOMY_LABELS, PILOT_STATUS_LABELS, listRow, pilotPath, pilotStatusClass } from "../lib/pilots";
import PilotHealthBadge from "./PilotHealthBadge";
import { TeamStack } from "./PilotTeam";
import { TableCell, TableRow } from "./ui/table";
import { ChevronRight } from "lucide-react";

const fmtDay = (value) => {
  if (!value) return null;
  const [y, m, d] = String(value).slice(0, 10).split("-");
  return `${d}/${m}/${y}`;
};

const StatusChip = ({ status }) => (
  <span className={`inline-flex rounded-full px-2 py-0.5 text-2xs font-semibold ${pilotStatusClass(status)}`} data-testid="pilot-row-status">
    {PILOT_STATUS_LABELS[status] || status}
  </span>
);

// A closed pilot has no live health: the badge is only shown for a running one.
const HealthCell = ({ row }) => (row.running ? <PilotHealthBadge state={row.healthState} testid="pilot-row-health" /> : <span className="text-xs text-slate-400">Hors suivi</span>);

function followupText(row) {
  if (!row.nextFollowup) return row.running ? "Aucun suivi planifié" : "";
  const due = fmtDay(row.nextFollowup.due);
  return due ? `${due} · ${row.nextFollowup.title}` : row.nextFollowup.title;
}

// Desktop table row. The whole row opens the workspace; the pharmacy name is the real link (keyboard, middle click).
export function PilotTableRow({ item, onOpen }) {
  const row = listRow(item);
  return (
    <TableRow className="cursor-pointer hover:bg-slate-50" onClick={() => onOpen(row.prospectId)} data-testid="pilot-row" data-pilot-id={row.id} data-health={row.healthState || "unknown"}>
      <TableCell>
        <Link to={pilotPath(row.prospectId)} onClick={(e) => e.stopPropagation()} className="font-medium text-slate-900 hover:underline" data-testid="pilot-row-name">{row.name}</Link>
        {row.place && <div className="text-xs text-slate-500">{row.place}</div>}
      </TableCell>
      <TableCell><StatusChip status={row.status} /></TableCell>
      <TableCell><HealthCell row={row} /></TableCell>
      <TableCell className="text-sm text-slate-700">{row.responsible || "Aucun responsable"}</TableCell>
      <TableCell><TeamStack members={row.team} total={row.teamSize} /></TableCell>
      <TableCell className="text-sm text-slate-700 max-w-[260px] truncate" title={followupText(row)}>{followupText(row)}</TableCell>
      <TableCell className="hidden xl:table-cell text-sm text-slate-600 max-w-[200px] truncate">{row.activity || "Aucune activité"}</TableCell>
      <TableCell className="hidden xl:table-cell text-sm text-slate-600">{AUTONOMY_LABELS[row.autonomy] || "Non évaluée"}</TableCell>
    </TableRow>
  );
}

// Mobile: two lines per pilot inside one list (name + health, then status · responsible · next follow-up).
export function PilotMobileRow({ item }) {
  const row = listRow(item);
  return (
    <Link to={pilotPath(row.prospectId)} className="flex items-center gap-3 px-3 py-2.5 border-b border-slate-100 last:border-b-0 active:bg-slate-50" data-testid="pilot-row" data-pilot-id={row.id} data-health={row.healthState || "unknown"}>
      <div className="min-w-0 flex-1 space-y-1">
        <div className="flex items-center justify-between gap-2">
          <span className="font-medium text-sm text-slate-900 truncate" data-testid="pilot-row-name">{row.name}</span>
          <HealthCell row={row} />
        </div>
        <div className="flex items-center gap-1.5 text-xs text-slate-500 min-w-0">
          <StatusChip status={row.status} />
          <span className="truncate">{[row.responsible, followupText(row)].filter(Boolean).join(" · ")}</span>
          {row.teamSize > 1 && <span className="ml-auto shrink-0"><TeamStack members={row.team} total={row.teamSize} max={2} /></span>}
        </div>
      </div>
      <ChevronRight size={16} className="text-slate-300 shrink-0" aria-hidden="true" />
    </Link>
  );
}
